import { initContract } from '@ts-rest/core'
import { z } from 'zod'
import {
	zCreateCouponRequest,
	zCreateCouponResponse,
	zGetCouponPathParams,
	zGetCouponResponse,
	zListCouponsRequest,
	zListCouponsResponse,
	zUpdateCouponStatusRequest,
	zUpdateCouponStatusResponse,
	zBatchCreateCouponRequest,
	zBatchCreateCouponResponse,
	zCouponMasterPathParams,
	zListCouponMastersRequest,
	zListCouponMastersResponse,
	zCouponMasterResponse,
	zListCouponUsageHistoryRequest,
	zListCouponUsageHistoryResponse,
	zCreateCouponUsageHistoryRequest,
	zCreateCouponUsageHistoryResponse,
	zDeleteCouponUsageHistoryResponse
} from '../schemas/coupon.api-use-case'
import { zCommonResponse } from '../schemas/common.schemas'

const c = initContract()

// ================= MEMBER COUPONS =================
export const CouponAPI = c.router(
	{
		listCoupons: {
			description: 'Get a list of coupons with optional filters',
			method: 'GET',
			path: '/',
			query: zListCouponsRequest,
			responses: {
				200: zListCouponsResponse
			}
		},

		listCouponUsageHistory: {
			description: 'Get usage history of coupons',
			method: 'GET',
			path: '/usage-history',
			query: zListCouponUsageHistoryRequest,
			responses: {
				200: zListCouponUsageHistoryResponse
			}
		},

		deleteCouponUsageHistory: {
			description: 'Delete a coupon usage history record',
			method: 'DELETE',
			path: '/usage-history/:usageId',
			pathParams: z.object({
				usageId: z.coerce.number()
			}),
			responses: {
				200: zDeleteCouponUsageHistoryResponse
			}
		},

		createCouponUsageHistory: {
			description: 'Create a new coupon usage history record',
			method: 'POST',
			path: '/usage-history',
			body: zCreateCouponUsageHistoryRequest,
			responses: {
				200: zCreateCouponUsageHistoryResponse
			}
		},

		createCoupon: {
			description: 'Create a new coupon for a member',
			method: 'POST',
			path: '/',
			body: zCreateCouponRequest,
			responses: {
				200: zCreateCouponResponse
			}
		},

		bulkCreateCoupons: {
			description: 'Bulk create coupons for active members',
			method: 'POST',
			path: '/bulk',
			body: zBatchCreateCouponRequest,
			responses: {
				200: zBatchCreateCouponResponse
			}
		},

		updateCoupon: {
			description: 'Update coupon fields (e.g. status)',
			method: 'PATCH',
			path: '/:couponId',
			pathParams: z.object({
				couponId: z.coerce.number() // prevents "batch"/"masters" issues
			}),
			body: zUpdateCouponStatusRequest,
			responses: {
				200: zUpdateCouponStatusResponse
			}
		},

		getCoupon: {
			description: 'Get coupon details',
			method: 'GET',
			path: '/:couponId',
			pathParams: z.object({
				couponId: z.coerce.number()
			}),
			responses: {
				200: zGetCouponResponse
			}
		}
	},
	{
		pathPrefix: '/m/coupons'
	}
)

// ================= COUPON MASTERS =================
export const CouponMasterAPI = c.router(
	{
		listCouponMasters: {
			description: 'Get all coupon master entries',
			method: 'GET',
			path: '/',
			query: zListCouponMastersRequest,
			responses: {
				200: zListCouponMastersResponse
			}
		},

		createCouponMaster: {
			description: 'Create a new coupon master entry',
			method: 'POST',
			path: '/',
			contentType: 'multipart/form-data',
			body: c.type<{
				couponType?: string
				title: string
				image?: File
				emojiDisplay?: string
				description?: string
				userGuide?: string
				sortOrder?: string
				validityType?: string
				defaultExpiryMonths?: string
				periodStartAt?: string
				periodEndAt?: string
				useLimitType?: string
				maxUsesPerMember?: string
				exchangePoints?: string
				isActive?: string
			}>(),
			responses: {
				200: zCouponMasterResponse
			}
		},

		updateCouponMaster: {
			description: 'Update a coupon master entry',
			method: 'PUT',
			path: '/:couponMasterId',
			pathParams: zCouponMasterPathParams,
			contentType: 'multipart/form-data',
			body: c.type<{
				couponType?: string
				title?: string
				image?: File
				emojiDisplay?: string
				description?: string
				userGuide?: string
				sortOrder?: string
				validityType?: string
				defaultExpiryMonths?: string
				periodStartAt?: string
				periodEndAt?: string
				useLimitType?: string
				maxUsesPerMember?: string
				exchangePoints?: string
				isActive?: string
			}>(),
			responses: {
				200: zCouponMasterResponse
			}
		},

		deleteCouponMaster: {
			description: 'Delete a coupon master entry (only if no coupons are linked)',
			method: 'DELETE',
			path: '/:couponMasterId',
			pathParams: zCouponMasterPathParams,
			body: c.noBody(),
			responses: {
				200: zCommonResponse,
				400: zCommonResponse
			}
		}
	},
	{
		pathPrefix: '/m/coupon-masters'
	}
)
